import { FC, useEffect, useRef, useState } from 'react';

import { Box, Button, Modal } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';

import { showErrorToast } from 'src/components/Toasts/toast';
import { contractTerms } from 'src/constants/contractTerms';
import { serviceRequirementsValidation } from 'src/constants/formValidations/addMerchantStep2';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { ProductOrderProvider } from 'src/contexts/ProductOrderProvider';
import { ServiceOrderProvider } from 'src/contexts/ServiceOrderProvider';
import { TransactionChargeProvider } from 'src/contexts/TransactionChargeProvider';
import { useAdditionalCharges } from 'src/hooks';
import { useTransactionData } from 'src/hooks/useTransactionCharges';
import { SalesConditions } from 'src/pages/AddMerchantPage/components/SalesConditions';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';
import { getMinimumContractTerm } from 'src/utils/utils';

import ProductOrders from '../../components/ProductOrders';
import { SelectedProducts, ServiceOrders } from '../../components/ServiceOrders';
import ServiceTotals from '../../components/ServiceTotals';
import { PricingServiceTypes, PricingTerms, ServiceType } from '../../components/ServiceType';
import { TransactionCharges } from '../../components/TransactionCharges';
import TransactionPricing from '../../components/TransactionPricing';
import { UpdateTransactionPricing } from '../UpdateTransactionPricing';

export type ContractTerm = {
    label: string;
    online: boolean;
    value: string;
    numberOfMonths: number;
};

export type ChargeValue = {
    purchasesPercentage?: string;
    purchasesCentsPerItem?: string;
    refundsPercentage?: string;
    refundsCentsPerItem?: string;
    amount?: string;
};

export type ChargesValue = {
    [key: string]: ChargeValue;
};

interface OrderItem {
    id: number;
    quantity: number;
    oneOffPrice: number | null;
    monthlyPrice: number | null;
    type: string;
    description: string;
}

export const ServiceRequirements: FC = () => {
    const salesConditionRef = useRef(null);
    const ordersRef = useRef(null);
    const serviceOrderRef = useRef(null);
    const [type, setType] = useState<PricingServiceTypes>();
    const { LOCALITY_CONSTANT } = useTransactionData();

    // Now pass serviceType to useAdditionalCharges hook
    const { additionalCharges } = useAdditionalCharges(type);

    const { addMerchant, merchantApplication, handleBack } = useAddMerchantContext();

    const [openTransactionCharges, setOpenTransactionCharges] = useState(false);

    const [editTransactionCharges, setEditTransactionCharges] = useState(false);
    const [valuesToUpdate, setValuesToUpdate] = useState(null);
    const [isSaveTPVisible, setIsSaveTPVisible] = useState(false);
    const [serviceType, setServiceType] = useState<PricingServiceTypes>();

    const initialValues = {
        salesConditions: {
            merchantCategoryCode: null,
            monthlyTurnover: '',
        },
        service: {
            serviceType: '',
            contractTerm: '',
            selectedTerminalOffer: '',
            selectedOnlineOffer: '',
            selectedTransactionOffer: '',
            transactionPricingOfferId: '',
            promoOffer: '',
        },
        order: {
            primary: [],
            secondary: [],
        },
        totals: { totalOneOff: null, totalMonthly: null, totalTerm: null },
        totalOrderPrice: {
            totalMonthly: [],
            totalOneOff: [],
        },
        transactionCharges: {
            personalCards: {} as ChargesValue,
            commercialCards: {} as ChargesValue,
            additionalCharges,
            locality: LOCALITY_CONSTANT,
            additionalTariffs: {} as ChargesValue,
            pciComplience: {} as ChargesValue,
        },
        isStepCompleted: false,
        ...merchantApplication?.serviceRequirements,
    };

    useEffect(() => {
        setType(serviceType);
    }, [serviceType]);

    const handleOpenTransactionCharges = () => {
        // useState is sync therefore it is advised to use the state value returned on the callback function
        setOpenTransactionCharges((previousState) => {
            return !previousState;
        });
    };

    const handleEditTransactionCharges = () => {
        setEditTransactionCharges((previousState) => {
            return !previousState;
        });
    };

    const checkOrderItems = (items: OrderItem[]) => {
        for (const item of items) {
            if (!item.quantity || item.oneOffPrice == null || item.monthlyPrice == null) {
                showErrorToast('Fill in required fields for Orders');
                ordersRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (serviceRequirements: object) => {
        const { primaryProducts, secondaryProducts } = serviceRequirements.productOrder;
        if (!checkOrderItems(primaryProducts) || !checkOrderItems(secondaryProducts)) {
            return;
        }

        await addMerchant({
            serviceRequirements: { ...serviceRequirements, isStepCompleted: true },
        });
    };
    const isEditingDisabled = isApplicationEditable(merchantApplication);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={handleSubmit}
            validationSchema={serviceRequirementsValidation}
        >
            {({ errors, values, setFieldValue }, formik: FormikProps<any>) => {
                const shouldShowOrders =
                    values.service.serviceType &&
                    values.service.contractTerm &&
                    (values.service.selectedOnlineOffer || values.service.selectedTerminalOffer) &&
                    values.service.serviceType !== PricingServiceTypes.ACQUIRING_ONLY;
                const chargeableContractTerm =
                    values.service.contractTerm === PricingTerms.CUSTOM
                        ? values.customTerm || 0
                        : contractTerms.find((term) => {
                              return term.value === values.service.contractTerm;
                          })?.numberOfMonths || 0;
                const minimumContractTermInMonths =
                    values.service.shouldApplyPromo === 'yes'
                        ? getMinimumContractTerm(values)
                        : chargeableContractTerm;

                if (values.service.serviceType !== serviceType) {
                    setServiceType(values.service.serviceType);
                }
                if (values.service.shouldApplyPromo === 'no' && values.service.promoOffer) {
                    setFieldValue('service.promoOffer', undefined);
                }
                const handleErrorToast = () => {
                    const hasFormikError = Object.keys(errors).length > 0;

                    if (hasFormikError) {
                        showErrorToast('Please fill in required fields');
                        if (
                            errors.salesConditions &&
                            (errors.salesConditions.merchantCategoryCode ||
                                errors.salesConditions.monthlyTurnover)
                        ) {
                            salesConditionRef.current.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                            });
                        }

                        if (
                            errors.service &&
                            (errors.service.serviceType ||
                                errors.service.contractTerm ||
                                errors.service.transactionPricingOfferId ||
                                errors.service.selectedOnlineOffer ||
                                errors.service.selectedTerminalOffer ||
                                errors.service.promoOffer)
                        ) {
                            serviceOrderRef.current.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                            });
                        }
                    }
                };

                return (
                    <Form>
                        <ProductOrderProvider>
                            <ServiceOrderProvider>
                                <TransactionChargeProvider>
                                    <Box display="flex" flexDirection="column" gap={3}>
                                        {/* Sales Conditions */}
                                        <Box
                                            display={
                                                openTransactionCharges || editTransactionCharges
                                                    ? 'none'
                                                    : ''
                                            }
                                            ref={salesConditionRef}
                                        >
                                            <SalesConditions formik={formik} />
                                        </Box>

                                        {/* Service Type */}
                                        <Box
                                            ref={serviceOrderRef}
                                            display={
                                                openTransactionCharges || editTransactionCharges
                                                    ? 'none'
                                                    : ''
                                            }
                                        >
                                            <ServiceType contractTerms={contractTerms} />
                                        </Box>
                                        {!openTransactionCharges && !editTransactionCharges && (
                                            <>
                                                {/* Service Order */}
                                                {shouldShowOrders && (
                                                    <>
                                                        <ServiceOrders
                                                            serviceType={
                                                                values.service
                                                                    .serviceType as PricingServiceTypes
                                                            }
                                                            order={values.order as SelectedProducts}
                                                            selectedOffer={
                                                                values.service
                                                                    .selectedOnlineOffer ||
                                                                values.service.selectedTerminalOffer
                                                            }
                                                            isEditingDisabled={isEditingDisabled}
                                                        />
                                                        <ServiceTotals
                                                            chargeableContractTerm={
                                                                chargeableContractTerm
                                                            }
                                                            minimumContractTerm={
                                                                minimumContractTermInMonths
                                                            }
                                                        />
                                                        <Box ref={ordersRef}>
                                                            <ProductOrders
                                                                serviceType={
                                                                    values.service
                                                                        .serviceType as PricingServiceTypes
                                                                }
                                                                isEditingDisabled={
                                                                    isEditingDisabled
                                                                }
                                                                contractTerm={
                                                                    chargeableContractTerm
                                                                }
                                                            />
                                                        </Box>
                                                    </>
                                                )}

                                                {/* Transaction Pricing */}
                                                <TransactionPricing
                                                    handleOpenTransactionCharges={
                                                        handleOpenTransactionCharges
                                                    }
                                                    handleEditTransactionCharges={
                                                        handleEditTransactionCharges
                                                    }
                                                />
                                            </>
                                        )}

                                        {/* Form buttons */}
                                        {!openTransactionCharges && !editTransactionCharges && (
                                            <Box display="flex" justifyContent="flex-end">
                                                <Button
                                                    sx={{
                                                        alignSelf: 'end',
                                                        marginTop: '24px',
                                                        marginRight: '22px',
                                                    }}
                                                    onClick={handleBack}
                                                    variant="back-button"
                                                >
                                                    Back
                                                </Button>

                                                <Button
                                                    sx={{
                                                        alignSelf: 'end',
                                                        marginTop: '24px',
                                                    }}
                                                    type="submit"
                                                    variant="main"
                                                    onClick={() => {
                                                        return handleErrorToast();
                                                    }}
                                                >
                                                    Next
                                                </Button>
                                            </Box>
                                        )}

                                        {/* Transaction Pricing Page */}
                                        {(openTransactionCharges || editTransactionCharges) && (
                                            <TransactionCharges
                                                editTransactionCharges={editTransactionCharges}
                                                handleOpenTransactionCharges={
                                                    handleOpenTransactionCharges
                                                }
                                                handleEditTransactionCharges={
                                                    handleEditTransactionCharges
                                                }
                                                name="transactionCharges"
                                                serviceType={values.service.serviceType}
                                                onSave={(updatedValues) => {
                                                    setValuesToUpdate(updatedValues);
                                                    setOpenTransactionCharges(false);
                                                    setEditTransactionCharges(false);
                                                }}
                                            />
                                        )}
                                        {isSaveTPVisible && (
                                            <Modal
                                                open
                                                onClose={() => {
                                                    setIsSaveTPVisible(false);
                                                    setValuesToUpdate(null);
                                                }}
                                            >
                                                {/* With the removal of the function that creates a replica transaction pricing offer with updated values if a user does so,
                        this modal becomes unreachable code */}
                                                <UpdateTransactionPricing
                                                    onClose={() => {
                                                        setIsSaveTPVisible(false);
                                                        setValuesToUpdate(null);
                                                    }}
                                                    valuesToUpdate={valuesToUpdate}
                                                />
                                            </Modal>
                                        )}
                                    </Box>
                                </TransactionChargeProvider>
                            </ServiceOrderProvider>
                        </ProductOrderProvider>
                    </Form>
                );
            }}
        </Formik>
    );
};
